import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Paperclip, Loader2, Upload, FileText, Download, Trash2, Users, Save, CloudDownload, AlertTriangle, CheckCircle2, Scale } from "lucide-react";
import { toast } from "sonner";
import { MissingDrawingsSpecsBanner } from "@/components/deal/MissingDrawingsSpecsBanner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/* Documents & notes attached to the deal (Anvil reads these). */
export function DocumentsCard({ deal, reload }) {
  const { user } = useAuth();
  const isOfficer = !!user;  // Dir 65 — roles flattened: any authenticated employee has full access.
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [note, setNote] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const fileRef = useRef(null);

  const loadDocs = () => api.get(`/bids/${deal.id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  const loadStatus = () => api.get(`/bids/${deal.id}/document-status`).then((r) => setStatus(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload docs/status when the deal id changes
  useEffect(() => { loadDocs(); loadStatus(); }, [deal.id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/bids/${deal.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`); loadDocs(); loadStatus(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const pullSam = async () => {
    setPulling(true);
    try {
      const r = await api.post(`/bids/${deal.id}/pull-sam-docs`, {});
      toast[r.data.added ? "success" : "message"](r.data.note || `Pulled ${r.data.added} document(s) from SAM.gov`);
      loadDocs(); loadStatus(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "SAM.gov pull failed"); }
    finally { setPulling(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/documents/note`, { description: note, title: noteTitle || "Note" });
      toast.success("Note added"); setNote(""); setNoteTitle(""); loadDocs(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (docId) => {
    try { await api.delete(`/bids/${deal.id}/documents/${docId}`); loadDocs(); loadStatus(); reload(); } catch { toast.error("Failed to remove"); }
  };

  const isFederal = deal.bid_type === "Federal";
  const hasManifest = status?.has_manifest;
  const missing = status?.missing || [];
  const complete = hasManifest && missing.length === 0;

  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-lg p-4 mt-5" data-testid="documents-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Paperclip className="h-4 w-4 text-zinc-900" /> Documents & notes <span className="text-[10px] font-normal text-zinc-400">(Anvil reads these)</span></h2>
        {isFederal && isOfficer && (
          <button data-testid="pull-sam-btn" onClick={pullSam} disabled={pulling} className="flex items-center gap-1.5 text-[11px] bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-500 disabled:opacity-50">
            {pulling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />} Pull from SAM.gov
          </button>
        )}
      </div>

      <MissingDrawingsSpecsBanner items={deal.shred?.missing_drawings_specs} className="mb-3" />

      {/* Document completeness — federal deals with a known attachment set */}
      {isFederal && hasManifest && (
        <div className="mb-3 border border-border rounded-md p-3 bg-muted/40" data-testid="doc-completeness">
          <div className="flex items-center gap-2 text-xs font-medium mb-2">
            {complete
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="text-emerald-700">All {status.expected_count} solicitation attachment(s) present</span></>
              : <><AlertTriangle className="h-4 w-4 text-amber-600" /> <span className="text-amber-700" data-testid="doc-missing-count">{status.pulled_count}/{status.expected_count} present · {missing.length} missing</span></>}
          </div>
          {missing.length > 0 && (
            <div className="space-y-1" data-testid="doc-missing-list">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Still missing — pull from SAM.gov or upload manually</div>
              {missing.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-700" data-testid={`doc-missing-${i}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" /> <span className="truncate">{f}</span>
                </div>
              ))}
            </div>
          )}
          {(status.needs_review || []).length > 0 && (
            <div className="mt-2 space-y-1" data-testid="doc-review-list">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Needs manual review (too large to auto-process)</div>
              {status.needs_review.map((d) => (
                <div key={d.id} className="text-[11px] text-zinc-600"><span className="font-medium">{d.original_filename}</span>{d.review_note ? ` — ${d.review_note}` : ""}</div>
              ))}
            </div>
          )}
          {status.sam_pull_note && (
            <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1" data-testid="doc-sam-note">{status.sam_pull_note}</div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="doc-file-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.dwg,.dxf" />
        <button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload PDF / DOC / SOW / spreadsheet
        </button>
      </div>
      {deal.bid_type === "Commercial" && (
        <div className="mb-3 space-y-1.5" data-testid="doc-note-block">
          <input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Note title (e.g. Scope explanation)" className="w-full bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 outline-none focus:border-gold-dark" data-testid="doc-note-title" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Or just explain the job in words (commercial) — Anvil will read it." rows={3} className="w-full bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 outline-none focus:border-gold-dark" data-testid="doc-note-text" />
          <button data-testid="doc-note-add" onClick={addNote} disabled={busy || !note.trim()} className="text-[11px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 px-3 py-1.5 rounded-md disabled:opacity-50">Add note</button>
        </div>
      )}
      <div className="space-y-1.5" data-testid="doc-list">
        {docs.length === 0 && <div className="text-xs text-zinc-400">No documents yet.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 border border-zinc-200 rounded-md px-2.5 py-1.5" data-testid={`doc-${d.id}`}>
            <FileText className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            <span className="text-xs text-zinc-800 truncate flex-1">{d.original_filename}</span>
            {d.content_type !== "text/note" && d.storage_path && (
              <a href={`${API_BASE}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-zinc-700" data-testid={`doc-dl-${d.id}`}><Download className="h-3.5 w-3.5" /></a>
            )}
            <button onClick={() => remove(d.id)} className="text-zinc-400 hover:text-red-500" data-testid={`doc-rm-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* LABOR-1 — DBA / certified-payroll labor breakout for the deal. */
const REGIME_LABEL = { "davis-bacon": "Davis-Bacon (DBA)", sca: "Service Contract Act (SCA)", commercial: "Commercial-item", none: "None" };
const WD_STATUS = {
  grounded: { t: "Grounded in attached WD", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  referenced_not_retrieved: { t: "WD referenced — table not pulled (not grounded)", c: "bg-amber-50 text-amber-700 border-amber-200" },
  attached_unreadable: { t: "WD attached but not machine-readable — OCR needed (not grounded)", c: "bg-amber-50 text-amber-700 border-amber-200" },
  not_found: { t: "WD not attached (not grounded)", c: "bg-red-50 text-red-700 border-red-200" },
  not_required: { t: "No prevailing wage required", c: "bg-zinc-50 text-zinc-600 border-zinc-200" },
};

export function LaborBreakout({ deal }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/bids/${deal.id}/labor-breakout`).then((r) => setData(r.data)).catch(() => setData({ error: true })); /* eslint-disable-next-line */ }, [deal.id]);
  if (!data) return <div className="bg-white border border-zinc-200 rounded-lg p-4 text-xs text-zinc-400 animate-pulse" data-testid="labor-breakout">Loading labor basis…</div>;
  if (data.error) return null;
  const st = WD_STATUS[data.status] || WD_STATUS.not_found;
  const classes = data.classifications || [];
  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-lg p-4" data-testid="labor-breakout">
      <h2 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2"><Scale className="h-4 w-4 text-zinc-900" /> Labor &amp; wage basis <span className="text-[10px] font-normal text-zinc-400">(DBA / certified payroll)</span></h2>
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
        <span className="px-2 py-0.5 rounded-full border border-zinc-300 font-semibold text-zinc-800" data-testid="labor-regime">{REGIME_LABEL[data.regime] || data.regime}</span>
        {data.override && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Officer override</span>}
        <span className={`px-2 py-0.5 rounded-full border ${st.c}`} data-testid="labor-wd-status">{st.t}</span>
        {data.wd_number && <span className="font-mono text-zinc-600">WD {data.wd_number}</span>}
        {data.source && <span className="text-zinc-400">· {data.source}</span>}
      </div>
      {data.note && <p className="text-[11px] text-zinc-500 mb-3">{data.note}</p>}

      {data.required && classes.length > 0 && (
        <div className="mb-3" data-testid="labor-classifications">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Prevailing wage classifications ({data.classification_count}) — from the attached WD</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="text-left text-zinc-400 border-b border-zinc-100"><th className="py-1 pr-3 font-medium">Classification</th><th className="py-1 pr-3 font-medium">Base</th><th className="py-1 pr-3 font-medium">Fringe</th><th className="py-1 font-medium">Total/hr</th></tr></thead>
              <tbody>
                {classes.map((c, i) => (
                  <tr key={i} className="border-b border-zinc-50" data-testid={`labor-class-${i}`}>
                    <td className="py-1 pr-3 text-zinc-800">{c.classification}</td>
                    <td className="py-1 pr-3 font-mono text-zinc-600">${c.base_rate.toFixed(2)}</td>
                    <td className="py-1 pr-3 font-mono text-zinc-600">${c.fringe.toFixed(2)}</td>
                    <td className="py-1 font-mono font-semibold text-zinc-900">${c.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data.required && classes.length === 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="labor-no-classes">
          Prevailing wage applies but no classification rate table is in the pulled documents. Pull the wage determination or upload it so labor is grounded.
        </p>
      )}

      {(data.labor_lines || []).length > 0 && (
        <div data-testid="labor-lines">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Labor in the priced cost model</div>
          {data.labor_lines.map((l, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] py-0.5" data-testid={`labor-line-${i}`}>
              <span className="text-zinc-700 truncate">{l.item}{l.basis ? <span className="text-zinc-400"> · {l.basis}</span> : null}</span>
              <span className="font-mono text-zinc-900 shrink-0 ml-2">{typeof l.amount === "number" ? `$${l.amount.toLocaleString()}` : (l.amount || "—")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const CPI_FIELDS = [
  ["contact_name", "Contact name"], ["contact_title", "Contact title"],
  ["classifications", "Work classifications"], ["project_name", "Project name"],
  ["project_address", "Project address"], ["contract_number", "Contract number"],
  ["wage_rates", "Wage rates"], ["fringe_rates", "Fringe rates"],
];

/* Crew & certified payroll (only on Won projects). */
export function CrewPayroll({ deal, users, reload }) {
  const [assigned, setAssigned] = useState(deal.assigned_employees || []);
  const [cpi, setCpi] = useState(deal.certified_payroll_info || {});
  const toggle = (name) => setAssigned((a) => a.includes(name) ? a.filter((x) => x !== name) : [...a, name]);
  const saveCrew = async () => { await api.put(`/bids/${deal.id}/assign`, { assigned_employees: assigned }); toast.success("Crew saved"); reload(); };
  const saveCpi = async () => { const r = await api.put(`/bids/${deal.id}/certified-payroll`, { certified_payroll_info: cpi }); toast.success(`Saved · ${r.data.missing.length} field(s) MISSING`); reload(); };
  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-lg p-4" data-testid="crew-payroll">
      <h2 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-zinc-900" /> Crew & certified payroll <span className="text-[10px] font-normal text-zinc-400">(won project)</span></h2>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Assigned employees</div>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="assign-employees">
        {users.map((u) => (
          <button key={u.user_id} data-testid={`assign-${u.user_id}`} onClick={() => toggle(u.name)} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${assigned.includes(u.name) ? "bg-gold/15 border-gold-dark text-zinc-900" : "border-zinc-300 text-zinc-500"}`}>{u.name}</button>
        ))}
        {users.length === 0 && <span className="text-[11px] text-zinc-400">No users yet.</span>}
      </div>
      <button data-testid="save-crew" onClick={saveCrew} className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900 mb-4"><Save className="h-3 w-3" /> Save crew</button>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Certified payroll info (flag MISSING, never guess)</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cpi-fields">
        {CPI_FIELDS.map(([k, label]) => {
          const v = cpi[k];
          const empty = !v || (Array.isArray(v) && !v.length) || (typeof v === "string" && !v.trim());
          return (
            <label key={k} className="block">
              <span className="text-[10px] text-zinc-500 flex items-center gap-1">{label}{empty && <span className="text-red-500 font-semibold">MISSING</span>}</span>
              <input data-testid={`cpi-${k}`} value={Array.isArray(v) ? v.join(", ") : (v || "")} onChange={(e) => setCpi({ ...cpi, [k]: e.target.value })} className="w-full mt-0.5 bg-white border border-zinc-300 rounded px-2 py-1 text-xs text-zinc-900 focus:border-gold-dark outline-none" />
            </label>
          );
        })}
      </div>
      <button data-testid="save-cpi" onClick={saveCpi} className="text-[11px] flex items-center gap-1 text-zinc-600 hover:text-zinc-900 mt-3"><Save className="h-3 w-3" /> Save payroll info</button>
    </div>
  );
}
